import type { BannerOutput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import type { DatetimeStr } from "@/models";
import {
	useCreateBanner,
	useDeleteBanner,
	useGetGenericBanners,
	useUpdateBanner,
} from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	ActionItem,
	ActionMenu,
	Button,
	Card,
	CardContent,
	DatePickerSingle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Skeleton,
	Typography,
} from "@st-marketplace/ui";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { z } from "zod";

type BannerFormData = {
	text: string;
	starts_at: string;
	ends_at: string | null;
};

const DateTimeField: React.FC<{
	value: string | null;
	onChange: (iso: string | null) => void;
	datePlaceholder?: string;
	timePlaceholder?: string;
}> = ({ value, onChange, datePlaceholder, timePlaceholder }) => {
	const date = value ? DateTime.fromISO(value).toJSDate() : undefined;
	const time = value ? DateTime.fromISO(value).toFormat("HH:mm") : "";

	const handleDateChange = (next: Date | undefined) => {
		if (!next) {
			onChange(null);
			return;
		}
		const base =
			value && DateTime.fromISO(value).isValid
				? DateTime.fromISO(value)
				: DateTime.fromJSDate(next).startOf("day");
		onChange(
			base
				.set({
					year: next.getFullYear(),
					month: next.getMonth() + 1,
					day: next.getDate(),
				})
				.toISO(),
		);
	};

	const handleTimeChange = (nextTime: string) => {
		if (!nextTime) {
			onChange(value);
			return;
		}
		const [hours, minutes] = nextTime.split(":").map(Number);
		const base =
			value && DateTime.fromISO(value).isValid
				? DateTime.fromISO(value)
				: DateTime.now().startOf("day");
		onChange(base.set({ hour: hours, minute: minutes }).toISO());
	};

	return (
		<div className="flex gap-2">
			<DatePickerSingle
				value={date}
				onChange={handleDateChange}
				placeholder={datePlaceholder}
				clearable
				className="flex-1"
			/>
			<Input
				type="time"
				aria-label={timePlaceholder}
				value={time}
				onChange={(e) => handleTimeChange(e.target.value)}
				className="w-32 shrink-0"
			/>
		</div>
	);
};

const BannerFormFields: React.FC<{
	form: UseFormReturn<BannerFormData>;
}> = ({ form }) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col gap-4">
			<FormField
				control={form.control}
				name="text"
				render={({ field }) => (
					<FormItem>
						<FormLabel required>{t("admin.banners.fields.text")}</FormLabel>
						<FormControl>
							<textarea
								{...field}
								rows={3}
								placeholder={t("admin.banners.fields.text")}
								className="min-h-20 w-full rounded-md border border-secondary-border bg-transparent px-3 py-2 text-sm outline-none placeholder:text-secondary-text-disabled focus:border-primary-border"
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name="starts_at"
				render={({ field }) => (
					<FormItem>
						<FormLabel required>{t("admin.banners.fields.startsAt")}</FormLabel>
						<FormControl>
							<DateTimeField
								value={field.value}
								onChange={(iso) => field.onChange(iso ?? "")}
								datePlaceholder={t("admin.banners.fields.startsAt")}
								timePlaceholder={t("admin.banners.fields.startsAt")}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name="ends_at"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t("admin.banners.fields.endsAt")}</FormLabel>
						<FormControl>
							<DateTimeField
								value={field.value}
								onChange={field.onChange}
								datePlaceholder={t("admin.banners.fields.endsAt")}
								timePlaceholder={t("admin.banners.fields.endsAt")}
							/>
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);
};

const BannerRowAction: React.FC<{
	banner: BannerOutput;
	onEdit: (banner: BannerOutput) => void;
	onDelete: (banner: BannerOutput) => void;
}> = ({ banner, onEdit, onDelete }) => {
	const { t } = useTranslation();
	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible
		<div onClick={(event) => event.stopPropagation()}>
			<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
				<ActionItem
					leadingIconName="edit-line"
					onClick={() => {
						setMenuActionOpen(false);
						onEdit(banner);
					}}
				>
					{t("action.edit")}
				</ActionItem>
				<ActionItem
					leadingIconName="delete-bin-line"
					onClick={() => {
						setMenuActionOpen(false);
						onDelete(banner);
					}}
				>
					{t("action.delete")}
				</ActionItem>
			</ActionMenu>
		</div>
	);
};

const STAdminBanners = () => {
	const { t, lang } = useTranslation();
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [editingBanner, setEditingBanner] = useState<BannerOutput | null>(null);
	const [deletingBanner, setDeletingBanner] = useState<BannerOutput | null>(
		null,
	);

	const createMutation = useCreateBanner();
	const updateMutation = useUpdateBanner();
	const deleteMutation = useDeleteBanner();

	const { data: banners, isLoading } = useGetGenericBanners();

	const bannerSchema = useMemo(
		() =>
			z.object({
				text: z.string().min(1, t("validation.required")),
				starts_at: z.string().min(1, t("validation.required")),
				ends_at: z.string().nullable().optional(),
			}),
		[t],
	);

	const createForm = useForm<BannerFormData>({
		resolver: zodResolver(bannerSchema),
		defaultValues: {
			text: "",
			starts_at: "",
			ends_at: null,
		},
	});

	const editForm = useForm<BannerFormData>({
		resolver: zodResolver(bannerSchema),
		defaultValues: {
			text: "",
			starts_at: "",
			ends_at: null,
		},
	});

	useEffect(() => {
		if (!editingBanner) return;
		editForm.reset({
			text: editingBanner.text,
			starts_at: editingBanner.starts_at,
			ends_at: editingBanner.ends_at ?? null,
		});
	}, [editingBanner, editForm]);

	const handleCreateSubmit = createForm.handleSubmit((values) => {
		createMutation.mutate(
			{
				body: {
					text: values.text,
					starts_at: values.starts_at as DatetimeStr,
					ends_at: values.ends_at ? (values.ends_at as DatetimeStr) : null,
				},
			},
			{
				onSuccess: () => {
					createForm.reset();
					setIsCreateOpen(false);
				},
			},
		);
	});

	const handleEditSubmit = editForm.handleSubmit((values) => {
		if (!editingBanner) return;
		updateMutation.mutate(
			{
				path: { banner_id: editingBanner.id },
				body: {
					text: values.text,
					starts_at: values.starts_at as DatetimeStr,
					ends_at: values.ends_at ? (values.ends_at as DatetimeStr) : null,
				},
			},
			{
				onSuccess: () => {
					editForm.reset();
					setEditingBanner(null);
				},
			},
		);
	});

	const handleDelete = () => {
		if (!deletingBanner) return;
		deleteMutation.mutate(
			{ path: { banner_id: deletingBanner.id } },
			{
				onSuccess: () => {
					setDeletingBanner(null);
				},
			},
		);
	};

	return (
		<div className="flex flex-col gap-5">
			<div className="flex items-center justify-between gap-2">
				<div>
					<Typography variant="title-sm" as="h2">
						{t("admin.banners.title")}
					</Typography>
					<Typography variant="body-sm" className="text-secondary-text-100">
						{t("admin.banners.description")}
					</Typography>
				</div>

				<Button
					type="button"
					color="primary"
					className="normal-case"
					startIcon={<Icon name="add-line" />}
					onClick={() => setIsCreateOpen(true)}
				>
					{t("admin.banners.createButton")}
				</Button>
			</div>

			{isLoading ? (
				<div className="flex flex-col gap-3">
					<Skeleton className="h-24 w-full rounded-xl" />
					<Skeleton className="h-24 w-full rounded-xl" />
				</div>
			) : banners && banners.length > 0 ? (
				banners.map((banner) => (
					<Card key={banner.id} className="mb-3">
						<CardContent className="p-5">
							<div className="flex items-start justify-between gap-3">
								<div className="flex flex-1 flex-col gap-1.5">
									<Typography variant="body-md" className="font-medium">
										{banner.text}
									</Typography>
									<div className="flex flex-row items-center gap-3">
										<Typography variant="body-sm">
											{t("admin.banners.startsAt")}:{" "}
											{getTimeSinceText(banner.starts_at, t, lang)}
										</Typography>
										{banner.ends_at && (
											<Typography variant="body-sm">
												{t("admin.banners.endsAt")}:{" "}
												{getTimeSinceText(banner.ends_at, t, lang)}
											</Typography>
										)}
									</div>
								</div>
								<BannerRowAction
									banner={banner}
									onEdit={setEditingBanner}
									onDelete={setDeletingBanner}
								/>
							</div>
						</CardContent>
					</Card>
				))
			) : (
				<Typography variant="body-sm">
					{t("admin.banners.noBanners")}
				</Typography>
			)}

			{/* Create Dialog */}
			<Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
				<DialogContent className="w-full max-w-lg">
					<DialogHeader>
						<DialogTitle>{t("admin.banners.createTitle")}</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						<Form {...createForm}>
							<form
								noValidate
								onSubmit={handleCreateSubmit}
								id="banner-create-form"
							>
								<BannerFormFields form={createForm} />
							</form>
						</Form>
					</DialogScrollableArea>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("button.cancel"),
								props: {
									type: "button",
									disabled: createMutation.isPending,
									onClick: () => setIsCreateOpen(false),
								},
							},
							primary: {
								label: t("button.submit"),
								props: {
									type: "submit",
									form: "banner-create-form",
									loading: createMutation.isPending,
									disabled: createMutation.isPending,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* Edit Dialog */}
			<Dialog
				open={Boolean(editingBanner)}
				onOpenChange={(o) => !o && setEditingBanner(null)}
			>
				<DialogContent className="w-full max-w-lg">
					<DialogHeader>
						<DialogTitle>{t("admin.banners.editTitle")}</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						<Form {...editForm}>
							<form
								noValidate
								onSubmit={handleEditSubmit}
								id="banner-edit-form"
							>
								<BannerFormFields form={editForm} />
							</form>
						</Form>
					</DialogScrollableArea>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("button.cancel"),
								props: {
									type: "button",
									disabled: updateMutation.isPending,
									onClick: () => setEditingBanner(null),
								},
							},
							primary: {
								label: t("button.submit"),
								props: {
									type: "submit",
									form: "banner-edit-form",
									loading: updateMutation.isPending,
									disabled: updateMutation.isPending,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation */}
			<Dialog
				open={Boolean(deletingBanner)}
				onOpenChange={(o) => !o && setDeletingBanner(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t("admin.banners.deleteConfirm")}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setDeletingBanner(null),
								},
							},
							primary: {
								label: t("action.button.delete"),
								props: {
									type: "button",
									loading: deleteMutation.isPending,
									onClick: handleDelete,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default STAdminBanners;
